import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useDeleteTourPlan } from '../hooks/useDeleteTourPlan'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { HiExclamation, HiOutlineTrash } from 'react-icons/hi'
import { useSelector } from 'react-redux'

import { AlertDialogBody, AlertDialogFooter, Box, Flex } from '@chakra-ui/react'

import { BasicAlertDialog, Button } from '@/components/ui'
import { selectMe } from '@/redux/me/meSlice'
import { mixpanel } from '@/utils/analytics'

interface DeleteTourPlanModalIProps {
  isOpen: boolean
  tourPlanId: string
  facilityId: string
  name: string
  onClose: () => void
  onDelete: () => void
}

export const DeleteTourPlanModal = ({
  isOpen,
  tourPlanId,
  facilityId,
  name,
  onClose,
  onDelete,
}: DeleteTourPlanModalIProps) => {
  const me = useSelector(selectMe)
  const { shouldEnableDeleteTourPlan } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const { deleteTourPlan, isDeleteLoading } = useDeleteTourPlan()
  const { setHighlightedDeviceIds } = useFloorPlan()

  const onSubmit = async () => {
    mixpanel.track('Button Press: Tour Plan Details - Delete Tour Save', {
      facility_id: facilityId,
      tour_plan_id: tourPlanId,
      role: me.roleName,
    })
    try {
      await deleteTourPlan(tourPlanId)
      showSuccess(`Successfully deleted tour plan ${name}.`)
      setHighlightedDeviceIds(null)
      onClose()
      onDelete()
    } catch {
      showError(`Unable to delete tour plan ${name}`)
    }
  }

  const handleCancel = () => {
    mixpanel.track('Button Press: Tour Plan Details - Delete Tour Cancel', {
      facility_id: facilityId,
      tour_plan_id: tourPlanId,
      role: me.roleName,
    })
    onClose()
  }

  return (
    <BasicAlertDialog
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      title='Delete TourPlan'
    >
      <AlertDialogBody>
        <Box
          color='#2d2e41'
          data-testid='tourplanPanel_deleteModal_message'
          fontSize='16px'
          fontWeight='500'
          letterSpacing='-0.53px'
        >
          Are you sure you want to delete this tourplan?
        </Box>
        <Flex alignItems='center' color='#D11D3A' flexDirection='row' pt='4'>
          <HiExclamation size='24px' />
          <Box fontSize='16px' fontWeight='medium' ml='1'>
            <Box as='span' fontWeight='bold'>
              Warning:
            </Box>{' '}
            All deletions are permanent
          </Box>
        </Flex>
      </AlertDialogBody>
      <AlertDialogFooter>
        <Button
          data-testid='tourplanPanel_deleteModal_cancelButton'
          mr='3'
          onClick={handleCancel}
          variant='ghost'
        >
          Cancel
        </Button>
        <Button
          data-testid='tourplanPanel_deleteModal_deleteButton'
          isDisabled={isDeleteLoading || !shouldEnableDeleteTourPlan}
          isLoading={isDeleteLoading}
          leftIcon={<HiOutlineTrash size='24px' />}
          onClick={onSubmit}
          variant='danger'
        >
          Delete
        </Button>
      </AlertDialogFooter>
    </BasicAlertDialog>
  )
}
